import {
  Auth,
  FoundationAnalytics,
  FoundationAnalyticsEvent,
  FoundationAnalyticsEventType,
  logger,
  Message,
  Session,
  SocketSubscription,
} from '@genesislcap/foundation-comms';
import { Constructable, Observable, observable, Splice } from '@microsoft/fast-element';
import { Container } from '@microsoft/fast-foundation';
import { Route, RouterConfiguration } from '@microsoft/fast-router';
import {defaultLayout, loginBackground, loginLayout, loginLogo, noMenusLayout} from '../layouts';
import { SellerDashboard } from './seller-dashboard/seller-dashboard';
// import { PostTrade } from './post-trade/post-trade';
// import { Reports } from './reports/reports';
import { NotFound } from './not-found/not-found';
import { BuyerDashboard } from './buyer-dashboard/buyer-dashboard';
import { IndividualProject } from './individual-project/individual-project';
import { MarketDepth } from './market-depth/market-depth';
import { SellerRfq } from './seller-rfq/seller-rfq';
import { BuyerAnalytics } from './buyer-analytics/buyer-analytics';
import { ICompanyService } from '../services/company.service';
import { IEnumService } from '../services/enum.service';
import { INavigationService} from '../services/navigation.service';
import { UserManagement } from './user-management/user-management';
import { Execution } from './execution/execution';
import { IArteriaNegotiation, LoginSettings, INegotiation } from '../types';
import { CompanyRole, getKeyByValue, PROFILE_RIGHTS, RoutePath } from '../utils/enums/generic.enum';
import { Acknowledgement } from './acknowledgement/acknowledgement';
import { IRfqService, IVppaService } from '../services';
import { hasLegalOrAuthorizedSignatoryVisibility, Profile } from '../utils/profiles';
import { IAuthService } from '../services/auth.service';
import { INotificationService } from '../services/notification.service';
import { IUserService } from '../services/user.service';
import { Welcome } from './welcome/welcome';
import { IExecutionService } from '../services/execution.service';
import { LandingPage } from '../components/page-specific/landing-page/landing-page';
import { COMPANY_ROLE } from '../components/mf/companys/edit';

const PERMISSIONS_VIEW = new Map<RoutePath, PROFILE_RIGHTS>([
  [RoutePath.BUYER_DASHBOARD, PROFILE_RIGHTS.BUYER_DASHBOARD_VIEW],
  [RoutePath.SELLER_DASHBOARD, PROFILE_RIGHTS.SELLER_DASHBOARD_VIEW],
  [RoutePath.MARKET_DEPTH, PROFILE_RIGHTS.MARKET_DEPTH_VIEW],
  [RoutePath.BUYER_ANALYTICS, PROFILE_RIGHTS.BUYER_ANALYTICS_VIEW],
  [RoutePath.SELLER_ANALYTICS, PROFILE_RIGHTS.SELLER_ANALYTICS_VIEW],
  [RoutePath.EXECUTION, PROFILE_RIGHTS.EXECUTION_VIEW],
  [RoutePath.EXPLORE, PROFILE_RIGHTS.EXPLORE_VIEW],
  [RoutePath.USER_MANAGEMENT, PROFILE_RIGHTS.USER_MANAGEMENT_VIEW],
//   [RoutePath.ACKNOWLEDGEMENT, PROFILE_RIGHTS.COMPANY_MANAGEMENT],
  [RoutePath.PROJECT_OVERVIEW, PROFILE_RIGHTS.PROJECT_OVERVIEW_VIEW],
]);

const CLEANTRADE_NAV = [
  {
    index: 1,
    path: RoutePath.SELLER_DASHBOARD,
    title: `Dashboard`,
    variant: 'solid',
    right: PROFILE_RIGHTS.SELLER_DASHBOARD_VIEW,
  },
  {
    index: 1,
    path: RoutePath.BUYER_DASHBOARD,
    title: `Dashboard`,
    variant: 'solid',
    right: PROFILE_RIGHTS.BUYER_DASHBOARD_VIEW,
  },
  {
    index: 2,
    path: RoutePath.MARKET_DEPTH,
    title: 'Market Depth',
    variant: 'solid',
    right: PROFILE_RIGHTS.MARKET_DEPTH_VIEW,
  },
  {
    index: 6,
    path: RoutePath.EXECUTION,
    title: 'Execution',
    variant: 'solid',
    right: PROFILE_RIGHTS.EXECUTION_VIEW,
  },
  {
    index: 7,
    path: RoutePath.SELLER_ANALYTICS,
    title: 'Analytics',
    variant: 'solid',
    right: PROFILE_RIGHTS.SELLER_ANALYTICS_VIEW,
  },
  {
    index: 7,
    path: RoutePath.BUYER_ANALYTICS,
    title: 'Analytics',
    variant: 'solid',
    right: PROFILE_RIGHTS.BUYER_ANALYTICS_VIEW,
  },
  {
    index: 8,
    path: RoutePath.USER_MANAGEMENT,
    title: 'Admin',
    variant: 'solid',
    right: PROFILE_RIGHTS.USER_MANAGEMENT_VIEW,
  },
];
const CLEANSIGHT_NAV = [
  {
    index: 3,
    title: 'Discover',
    variant: 'solid',
    right: PROFILE_RIGHTS.EXPLORE_VIEW,
  },
  {
    index: 9,
    title: 'Manage',
    variant: 'solid',
    right: PROFILE_RIGHTS.POST_TRADE_VIEW,
    isPostTradeView: true,
  }];

export class MainRouterConfig extends RouterConfiguration<LoginSettings> {
  @IUserService userService: IUserService;
  @ICompanyService companyService: ICompanyService;
  @IExecutionService executionService: IExecutionService;
  @IAuthService authService: IAuthService;
  @observable currentPath: string = '';
  @observable arteriaNegotiations: IArteriaNegotiation[];
  @observable autoExecutedNegotiations: INegotiation[];
  @observable totalNegotiationCount: number = 0;
  @IEnumService enumService: IEnumService;
  @INotificationService notificationService: INotificationService;
  @IRfqService rfqService: IRfqService;
  @IVppaService vppaService: IVppaService;
  @INavigationService navigationService: INavigationService;
  @observable remap_url: String;
  @observable enableExplore: boolean = false;
  @observable isAdvisor: boolean = false;

  constructor(
    @Auth private auth: Auth,
    @Container private container: Container,
    @FoundationAnalytics private analytics: FoundationAnalytics,
    @Session private session: Session,
  ) {
    super();
    document.addEventListener('click', this.updateLastInteractionTime);
    document.addEventListener('mousemove', this.updateLastInteractionTime);
    document.addEventListener('keypress', this.updateLastInteractionTime);
  }

  public allRoutes = (): Array<any> => {
    return this.filterNavigation([...CLEANTRADE_NAV, ...CLEANSIGHT_NAV]);
  };

  public allCleanTradeRoutes = (): Array<any> => {
    return this.filterNavigation(CLEANTRADE_NAV);
  };

  public allCleanSightRoutes = (): Array<any> => {
    return this.filterNavigation(CLEANSIGHT_NAV);
  };

  private filterNavigation(nav: Array<any>) {
    nav = nav.filter((n) => this.userService.getFilteredPermissions().includes(n.right));
    if (!this.isAdvisor) {
      nav = nav.filter((e) => e.path !== RoutePath.USER_MANAGEMENT);
    }
    // Legal and Authorized Signatory
    if (hasLegalOrAuthorizedSignatoryVisibility(this.userService.getFilteredPermissions())) {
      switch (this.companyService.getCurrentCompany()?.COMPANY_ROLE) {
        // TODO: Explain that exists a rule based on the company role
        case CompanyRole.BUYER_ONLY:
          nav = nav.filter((e) => e.right === PROFILE_RIGHTS.BUYER_DASHBOARD_VIEW);
          break;
        case CompanyRole.SELLER_ONLY:
          nav = nav.filter((e) => e.right === PROFILE_RIGHTS.SELLER_DASHBOARD_VIEW);
          break;
        default:
          break;
      }
    }

    if (nav.length === 0) {
      nav.push({
        index: 9,
        path: RoutePath.WELCOME,
        title: 'Welcome',
        variant: 'solid',
        right: null,
      });
    }

    return nav;
  }

  public configure() {
    this.title = 'CleanTrade';
    const commonSettings: LoginSettings = {
      public: false,
      autoAuth: true,
    };
    this.routes.map(
      {
        path: '',
        redirect: 'login',
      },
      {
        path: 'login',
        title: 'Login',
        name: 'login',
        element: async () => {
          const { configure, Login } = await import(
            /* webpackChunkName: "foundation-login" */
            '@genesislcap/foundation-login'
          );
          configure(this.container, {
            autoConnect: true,
            autoAuth: true,
            omitRoutes: ['request-account'],
            defaultRedirectUrl: 'dashboard',
            sso: {
              toggled: true,
              identityProvidersPath: 'gwf/sso/list',
            },
            logo: loginLogo,
            background: loginBackground,
          });
          return Login;
        },
        settings: {
          public: true,
        },
        layout: loginLayout(),
        childRouters: true,
      },
      {
        path: 'landing',
        element: LandingPage,
        title: 'Cascade',
        name: 'landing',
        settings: {
          public: true,
        },
        layout: noMenusLayout,
      },
      /*
        {
          path: 'reports',
          element: Reports,
          title: 'Reports',
          name: 'reports',
          settings: commonSettings,
        },
        */
      {
        path: 'dashboard',
        element: SellerDashboard,
        title: 'Dashboard',
        name: 'dashboard',
        settings: commonSettings,
      },
      {
        path: 'buyer-dashboard',
        element: BuyerDashboard,
        title: 'Dashboard',
        name: 'buyer-dashboard',
        settings: commonSettings,
      },
      {
        path: 'project/{id}',
        element: IndividualProject,
        title: 'Project',
        name: 'project',
        settings: commonSettings,
      },
      {
        path: 'market-depth',
        element: MarketDepth,
        title: 'Market Depth',
        name: 'market-depth',
        settings: commonSettings,
      },
      {
        path: 'execution',
        element: Execution,
        title: 'Execution',
        name: 'execution',
        settings: commonSettings,
      },
      {
        path: 'analytics',
        element: SellerRfq,
        title: 'Analytics',
        name: 'analytics',
        settings: commonSettings,
      },
      {
        path: 'buyer-analytics',
        element: BuyerAnalytics,
        title: 'Analytics',
        name: 'buyer-analytics',
        settings: commonSettings,
      },
      {
        path: 'not-found',
        element: NotFound,
        title: 'Not Found',
        name: 'not-found',
      },
      {
        path: 'user-management',
        element: UserManagement,
        title: 'Admin',
        name: 'user-management',
        settings: commonSettings,
      },
      /*
        {
          path: 'acknowledgement/{id}',
          element: Acknowledgement,
          title: 'User Agreement Acknowledgement',
          name: 'acknowledgement',
          layout: noMenusLayout,
          settings: commonSettings,
        },
        */
      {
        path: 'welcome',
        element: Welcome,
        title: 'Welcome',
        name: 'welcome',
        settings: commonSettings,
      },
    );

    const auth = this.auth;

    // Example of a FallbackRouteDefinition
    this.routes.fallback(() =>
      this.auth.isLoggedIn ? { redirect: 'not-found' } : { redirect: 'welcome' },
    );

    // Example of a NavigationContributor
    this.contributors.push({
      navigate: async (phase) => {
        const settings = phase.route.settings;
        this.currentPath = phase.route.endpoint.path;
        this.navigationService.setCurrentPath(this.currentPath);

        /**
         * TODO: Centralize
         * Suspect this should be done via createEventSink, but it's not fully clear how-to do that as no docs
         */
        this.analytics.trackEvent(FoundationAnalyticsEventType.routeChanged, <
          FoundationAnalyticsEvent.RouteChanged
        >{
          path: phase.route.endpoint.path,
        });

        // If public route don't block
        if (settings && settings.public) {
          this.companyService.clearCompany();
          return;
        }

        // If logged in don't block
        if (this.auth.isLoggedIn) {
          await this.setCurrentCompany();
          await this.userService.setFilteredPermissionsProfiles(
            this.companyService.getCurrentCompany().COMPANY_ROLE === COMPANY_ROLE.ADVISOR,
          );
          await this.userService.setCurrentUser();
          this.defaultLayout = defaultLayout;
          this.checkValidRoute(phase);
          this.getREmapUrlRoute();
          this.checkIdleTime();
          this.registerAuthNotify();
          this.registerExecutionNotifier();
          setTimeout(() => {
            this.isAdvisor =
              this.userService.hasManagementPageViewRight() &&
              (!this.userService.hasAdvisorRights() ||
                this.userService.getCurrentUser().COMPANY_ID ==
                  this.companyService.getCurrentCompany().CASCADE_COMPANY_ID);
          }, 500);
          return;
        }

        // If allowAutoAuth and session is valid try to connect + auto-login
        if (settings && settings.autoAuth && (await auth.reAuthFromSession())) {
          await this.setCurrentCompany();
          await this.userService.setFilteredPermissionsProfiles(
            this.companyService.getCurrentCompany().COMPANY_ROLE === COMPANY_ROLE.ADVISOR,
          );
          await this.userService.setCurrentUser();
          this.defaultLayout = defaultLayout;
          this.checkValidRoute(phase);
          this.getREmapUrlRoute();
          this.checkIdleTime();
          this.registerAuthNotify();
          this.registerExecutionNotifier();
          setTimeout(() => {
            this.isAdvisor =
              this.userService.hasManagementPageViewRight() &&
              (!this.userService.hasAdvisorRights() ||
                this.userService.getCurrentUser().COMPANY_ID ==
                  this.companyService.getCurrentCompany().CASCADE_COMPANY_ID);
          }, 500);
          return;
        }

        // Otherwise route them somewhere, like to a login
        phase.cancel(() => {
          this.session.captureReturnUrl();
          Route.name.replace(phase.router, 'login');
        });
      },
    });
  }

  public getREmapUrlRoute() {
    this.companyService
      .eventREmapOpen()
      .then((response) => {
        if (response && response.MESSAGE_TYPE === 'EVENT_ACK') {
          this.remap_url = JSON.parse(response.GENERATED[0].REMAP_URL).callbackUrl;
          this.enableExplore = true;
        }
      })
      .catch((error) => {
        logger.error(error);
      });
  }

  public construct<T>(Type: Constructable<T>): T {
    return this.container.get(Type) as T;
  }

  private checkValidRoute(phase: any) {
    if (!this.auth.loggedUserResult) {
      return;
    }

    /**
     * If the company linked to user on table USER_LAST_LOGIN has not assigned the agrrement yet,
     * if this user has company admin role, we redirect the user to assignment page,
     * if not we redirect to login page again
     * */
    if (
      !this.companyService.getCurrentCompany()?.SEF_PARTICIPANT_AGREEMENT_CONSENT &&
      !this.companyService.isNonSefParticipantAgreement()
    ) {
      const hasCompanyManagementPermission = this.userService
        .getFilteredPermissions()
        ?.find((permission) => permission === PROFILE_RIGHTS.COMPANY_MANAGEMENT);

      /*
      if (hasCompanyManagementPermission) {
        if (this.currentPath.includes(RoutePath.ACKNOWLEDGEMENT)) {
          return;
        }
        const path = `${RoutePath.ACKNOWLEDGEMENT}/${
          this.companyService.getCurrentCompany().CASCADE_COMPANY_ID
        }`;
        Route.path.push(path);
        return;
      }
      */

      this.logoutAndRedirectToLogin();
    }

    if (this.currentPath.includes(RoutePath.ACKNOWLEDGEMENT)) {
      return;
    }

    const validRoutes = this.allRoutes();

    // Welcome
    if (
      this.currentPath === RoutePath.WELCOME &&
      validRoutes.find((e) => e.path === RoutePath.WELCOME)
    ) {
      return;
    }

    // Right Views
    const right = PERMISSIONS_VIEW.get(
      getKeyByValue(this.getCurrentPath(this.currentPath), RoutePath),
    );

    if (!this.userService.getFilteredPermissions().includes(right)) {
      phase.cancel(() => {
        this.session.captureReturnUrl();
        Route.name.replace(phase.router, validRoutes[0].path);
      });
      return;
    }

    // Legal and Authorized Signatory
    if (hasLegalOrAuthorizedSignatoryVisibility(this.userService.getFilteredPermissions())) {
      const companyRole = this.companyService.getCurrentCompany()?.COMPANY_ROLE;

      if (
        companyRole === CompanyRole.BUYER_ONLY &&
        this.currentPath === RoutePath.SELLER_DASHBOARD
      ) {
        this.session.captureReturnUrl();
        Route.name.replace(phase.router, RoutePath.BUYER_DASHBOARD);
        return;
      }

      if (
        companyRole === CompanyRole.SELLER_ONLY &&
        this.currentPath === RoutePath.BUYER_DASHBOARD
      ) {
        this.session.captureReturnUrl();
        Route.name.replace(phase.router, RoutePath.SELLER_DASHBOARD);
        return;
      }
    }
  }

  private getCurrentPath(currentPath: string) {
    return currentPath.includes('project') ? 'project' : currentPath;
  }

  public getRemapUrl() {
    // Enabled everytime when explore button is clicked
    this.enableExplore = false;
    setTimeout(() => this.getREmapUrlRoute(), 500);
    return this.remap_url;
  }

  private async setCurrentCompany() {
    if (!this.companyService.getCurrentCompany()?.CASCADE_COMPANY_ID) {
      await this.companyService.setCompany();
      await this.checkValidLogin();
    }
  }

  private async checkValidLogin() {
    await this.userService.getUser(this.auth.loggedUserResult.username).subscribe({
      next: (streamMessage: Message) => {
        const result = streamMessage.ROW ?? null;

        if (!result) {
          return;
        }

        if (result[0] && result[0].STATUS === 'DISABLED') {
          this.logoutAndRedirectToLogin();
        }
      },
      error: (error: any) => {
        this.logoutAndRedirectToLogin();
      },
    });

    // If the user do not have values on USER_LAST_LOGIN table, will be redirected to login page
    if (!this.companyService.getCurrentCompany()) {
      this.logoutAndRedirectToLogin();
    }

    // If the user do not have values on PROFILE_USER table, will be redirected to login page
    if (!this.auth.loggedUserResult.profiles || this.auth.loggedUserResult.profiles.length === 0) {
      this.logoutAndRedirectToLogin();
    }

    // If the user do not have values on USER_COMPANY table, will be redirected to login page
    await this.companyService.getAllUserCompanies().subscribe({
      next: (streamMessage: Message) => {
        const resultList = streamMessage.ROW ?? null;
        if (!resultList || resultList.length === 0) {
          this.logoutAndRedirectToLogin();
        }
      },
      error: (error: any) => {
        this.logoutAndRedirectToLogin();
      },
    });
  }

  private async logoutAndRedirectToLogin() {
    this.authService.logout();
  }

  private executionNotificationNotifier: any;
  private executionNotificationHandler: any;

  private registerExecutionNotifier() {
    this.executionService.startStreaming();
    const _this = this;
    this.executionNotificationNotifier = Observable.getNotifier(this.executionService);
    this.executionNotificationHandler = {
      handleChange() {
        _this.totalNegotiationCount = _this.executionService.executionNotifications.filter(
          (x) => x.NEGOTIATION_ID,
        ).length;
      },
    };

    this.executionNotificationNotifier.subscribe(
      this.executionNotificationHandler,
      'executionNotifications',
    );
  }

  private registerAuthNotify() {
    this.auth.isLoggedIn$.subscribe({
      next: (isLoggedIn: boolean) => {
        const currentPath = Route.path.current;
        if (!isLoggedIn && currentPath != '/login') {
          this.logoutAndRedirectToLogin();
        }
      },
      error: (error: any) => {},
    });
  }

  private idleTimeout;

  private updateLastInteractionTime = () => {
    this.authService.setLastInteractionTime();
  };

  private checkIdleTime = () => {
    const currentTime = new Date().getTime();
    const idleTime = currentTime - this.authService.getLastInteractionTime();
    if (idleTime >= this.authService.defaultIdletTimeout) {
      this.logoutAndRedirectToLogin();
    } else {
      this.scheduleIdleCheck();
    }
  };

  private scheduleIdleCheck() {
    clearTimeout(this.idleTimeout);
    this.idleTimeout = setTimeout(this.checkIdleTime, this.authService.defaultIdletTimeCheck);
  }
}
